import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Sparkles, X, Mic, Loader2, ImagePlus, Camera, Volume2, Square, Film, Youtube, Plus, Video, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatErr } from "@/lib/api";
import { useVoiceRecorder } from "@/lib/useVoiceRecorder";
import { fileToCompressedDataUrl } from "@/lib/imageCompress";
import { VideoFrameGrabber } from "@/components/VideoFrameGrabber";
import { ImageMarkup } from "@/components/ImageMarkup";
import { pasteToFiles } from "@/components/PhotoAddButtons";

const isTouchDevice = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(pointer: coarse)")?.matches ||
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || ""));

const AttachItem = ({ icon: Icon, label, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-secondary/60 transition text-left"
    data-testid={testid}
  >
    <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
    {label}
  </button>
);

/**
 * Floating "ask the ticket assistant" chat button — e.g. "what's my
 * ticket for today, what should I do now?" Available to EVERYONE who can
 * see tickets (admin, any worker, any colony contact) — unlike the
 * Shopping List assistant this isn't manager/admin-only. Positioned
 * bottom-right, matching the Shopping List assistant's FAB. Pass
 * `hidden` to unmount it while another bottom-right FAB (e.g. the
 * Shopping List assistant inside a dialog) is on screen at the same time.
 *
 * Vision: the user can paste, upload, or (on phones) snap a photo so the
 * AI can actually SEE what they're asking about. The backend also loads
 * photos already attached to the ticket the question refers to.
 */
export function TicketsAiButton({ client, askPath, T = (x) => x, hidden = false, calendarYear }) {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [turns, setTurns] = useState([]);
  const [images, setImages] = useState([]); // data URLs pending on the composer
  const [attaching, setAttaching] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null); // local object URL for the scrub-and-grab player
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const videoRef = useRef(null);
  const videoCaptureRef = useRef(null);
  const scrollRef = useRef(null);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [markupIdx, setMarkupIdx] = useState(null); // which pending image is being annotated
  const audioRef = useRef(null);
  const autoPlayedRef = useRef(new Set());
  const [speakingIdx, setSpeakingIdx] = useState(null); // turn currently playing
  const [speakLoadingIdx, setSpeakLoadingIdx] = useState(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [turns, asking]);
  const showCamera = isTouchDevice();

  // TTS ("read it aloud") is only wired for the admin + worker portals.
  const portalPrefix = askPath.split("/")[1];
  const ttsEnabled = portalPrefix === "mgmt" || portalPrefix === "worker";
  const speakPath = `/${portalPrefix}/voice/speak`;

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setSpeakingIdx(null);
  };

  const speak = async (text, idx) => {
    if (!ttsEnabled || !text) return;
    // Tapping the speaker of the currently-playing answer stops it.
    if (speakingIdx === idx) {
      stopAudio();
      return;
    }
    stopAudio();
    setSpeakLoadingIdx(idx);
    try {
      const { data } = await client.post(speakPath, { text });
      if (!data?.audio) return;
      if (!audioRef.current) audioRef.current = new Audio();
      const audio = audioRef.current;
      audio.src = `data:${data.mime || "audio/mp3"};base64,${data.audio}`;
      audio.onended = () => setSpeakingIdx(null);
      setSpeakingIdx(idx);
      await audio.play().catch(() => setSpeakingIdx(null));
    } catch {
      setSpeakingIdx(null);
    } finally {
      setSpeakLoadingIdx(null);
    }
  };

  useEffect(() => () => {
    stopAudio();
    setVideoUrl((url) => { if (url) URL.revokeObjectURL(url); return null; });
  }, []); // stop audio + free any local video URL on unmount

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type?.startsWith("image/"));
    if (!files.length) return;
    setAttaching(true);
    try {
      const room = Math.max(0, 4 - images.length);
      const picked = files.slice(0, room);
      const urls = await Promise.all(picked.map((f) => fileToCompressedDataUrl(f)));
      setImages((prev) => [...prev, ...urls.filter(Boolean)].slice(0, 4));
    } finally {
      setAttaching(false);
    }
  };

  const onFileInput = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };

  // Video: open a scrub-and-grab player so the worker can seek to the exact
  // moment and grab THAT frame. The video plays locally and is never
  // uploaded or stored — only the grabbed still frames go to the AI.
  const onVideoInput = (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setVideoUrl(URL.createObjectURL(file));
  };

  const closeGrabber = () => {
    setVideoUrl((url) => {
      if (url) URL.revokeObjectURL(url);
      return null;
    });
  };

  const addGrabbedFrame = (frame) => setImages((prev) => [...prev, frame].slice(0, 4));

  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

  const ask = async (overrideText) => {
    const q = (overrideText ?? question).trim();
    const imgs = images;
    if ((!q && imgs.length === 0) || asking) return;
    setQuestion("");
    setImages([]);
    setAsking(true);
    setTurns((prev) => [...prev, { q: q || T("(photo attached)"), a: null, images: imgs }]);
    try {
      const { data } = await client.post(askPath, { message: q, images: imgs, ...(calendarYear ? { calendar_year: calendarYear } : {}) });
      setTurns((prev) => prev.map((t, i) => (i === prev.length - 1 ? { ...t, a: data.answer, howto: data.howto_query || null } : t)));
      if (ttsEnabled && data.answer) {
        const idx = turns.length; // index of the turn we just pushed
        autoPlayedRef.current.add(idx);
        speak(data.answer, idx);
      }
    } catch (e) {
      const err = formatErr(e.response?.data?.detail) || e.message;
      setTurns((prev) => prev.map((t, i) => (i === prev.length - 1 ? { ...t, a: err } : t)));
    } finally {
      setAsking(false);
    }
  };

  // Voice endpoint lives at /{portal}/voice/transcribe — derive the
  // portal prefix from askPath (e.g. "/mgmt/colony-tickets/ask" -> "/mgmt").
  const voicePath = `/${askPath.split("/")[1]}/voice/transcribe`;
  const { recording, transcribing, toggle } = useVoiceRecorder(client, voicePath, (text) => {
    if (text) setQuestion((q) => (q ? `${q} ${text}` : text));
  });

  if (hidden) return null;

  const widget = (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[60] w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-105 transition"
        title={T("Ask the ticket assistant")}
        data-testid="tickets-ai-fab"
      >
        <Sparkles className="w-5 h-5" />
      </button>
      {open && (
        <div
          className="fixed bottom-24 right-6 z-[60] w-80 max-h-[70vh] flex flex-col border border-border rounded-sm bg-background shadow-2xl"
          data-testid="tickets-ai-panel"
          onPaste={pasteToFiles(addFiles)}
        >
          <div className="px-3 py-2 border-b border-border flex items-center justify-between shrink-0">
            <div className="text-sm font-semibold flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> {T("Ticket assistant")}
            </div>
            <button type="button" onClick={() => setOpen(false)} data-testid="tickets-ai-close">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 text-sm">
            {turns.length === 0 && (
              <p className="text-xs text-muted-foreground">
                {T('Try: "What\'s my ticket for today?" — or attach a photo and ask how to fix it.')}
              </p>
            )}
            {turns.map((t, i) => (
              <div key={i} className="space-y-1">
                {t.images?.length > 0 && (
                  <div className="flex flex-wrap gap-1 justify-end">
                    {t.images.map((src, k) => (
                      <img
                        key={k}
                        src={src}
                        alt=""
                        className="w-12 h-12 object-cover rounded-sm border border-border"
                        data-testid={`tickets-ai-turn-${i}-image-${k}`}
                      />
                    ))}
                  </div>
                )}
                <div className="font-semibold text-xs text-muted-foreground">{t.q}</div>
                <div className="whitespace-pre-wrap" data-testid={`tickets-ai-answer-${i}`}>
                  {t.a === null ? T("Thinking…") : t.a}
                </div>
                {ttsEnabled && t.a && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => speak(t.a, i)}
                      disabled={speakLoadingIdx === i}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition disabled:opacity-40"
                      title={speakingIdx === i ? T("Stop") : T("Read it aloud")}
                      data-testid={`tickets-ai-speak-${i}`}
                    >
                      {speakLoadingIdx === i ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : speakingIdx === i ? (
                        <Square className="w-3.5 h-3.5 fill-current" />
                      ) : (
                        <Volume2 className="w-3.5 h-3.5" />
                      )}
                      {speakingIdx === i ? T("Stop") : T("Listen")}
                    </button>
                    {t.howto && (
                      <a
                        href={`https://www.youtube.com/results?search_query=${encodeURIComponent(t.howto)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 font-medium transition"
                        title={T("Watch how-to videos")}
                        data-testid={`tickets-ai-howto-${i}`}
                      >
                        <Youtube className="w-3.5 h-3.5" />
                        {T("Watch how-to")}
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {images.length > 0 && (
            <div className="px-2 pt-2 flex flex-wrap gap-1.5 border-t border-border shrink-0" data-testid="tickets-ai-pending-images">
              {images.map((src, i) => (
                <div key={i} className="relative">
                  <img src={src} alt="" className="w-12 h-12 object-cover rounded-sm border border-border" />
                  <button
                    type="button"
                    onClick={() => setMarkupIdx(i)}
                    className="absolute -bottom-1.5 -left-1.5 w-4 h-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow tap-exempt"
                    title={T("Circle or arrow the problem spot")}
                    data-testid={`tickets-ai-markup-image-${i}`}
                  >
                    <Pencil className="w-2.5 h-2.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-foreground text-background flex items-center justify-center tap-exempt"
                    title={T("Remove")}
                    data-testid={`tickets-ai-remove-image-${i}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="p-2 border-t border-border flex gap-1.5 shrink-0 items-center">
            <input
              ref={galleryRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={onFileInput}
              data-testid="tickets-ai-file-input"
            />
            {showCamera && (
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={onFileInput}
                data-testid="tickets-ai-camera-input"
              />
            )}
            {ttsEnabled && (
              <input
                ref={videoRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={onVideoInput}
                data-testid="tickets-ai-video-input"
              />
            )}
            {ttsEnabled && showCamera && (
              <input
                ref={videoCaptureRef}
                type="file"
                accept="video/*"
                capture="environment"
                className="hidden"
                onChange={onVideoInput}
                data-testid="tickets-ai-video-capture-input"
              />
            )}

            {/* One "+" button opens a tidy Camera / Photos / Videos list —
                far neater than a row of icons, especially on a phone. */}
            <div className="relative shrink-0">
              {attachMenuOpen && (
                <>
                  <div className="fixed inset-0 z-[65]" onClick={() => setAttachMenuOpen(false)} data-testid="tickets-ai-attach-scrim" />
                  <div
                    className="absolute bottom-11 left-0 z-[66] w-44 rounded-md border border-border bg-background shadow-xl overflow-hidden py-1"
                    data-testid="tickets-ai-attach-menu"
                  >
                    {showCamera && (
                      <AttachItem icon={Camera} label={T("Take a photo")} testid="tickets-ai-menu-camera"
                        onClick={() => { setAttachMenuOpen(false); cameraRef.current?.click(); }} />
                    )}
                    {ttsEnabled && showCamera && (
                      <AttachItem icon={Video} label={T("Record a video")} testid="tickets-ai-menu-record"
                        onClick={() => { setAttachMenuOpen(false); videoCaptureRef.current?.click(); }} />
                    )}
                    <AttachItem icon={ImagePlus} label={T("Photos")} testid="tickets-ai-menu-photos"
                      onClick={() => { setAttachMenuOpen(false); galleryRef.current?.click(); }} />
                    {ttsEnabled && (
                      <AttachItem icon={Film} label={T("Videos")} testid="tickets-ai-menu-videos"
                        onClick={() => { setAttachMenuOpen(false); videoRef.current?.click(); }} />
                    )}
                  </div>
                </>
              )}
              <button
                type="button"
                onClick={() => setAttachMenuOpen((v) => !v)}
                disabled={attaching || images.length >= 4}
                className={`h-9 w-9 rounded-sm border flex items-center justify-center transition disabled:opacity-40 ${attachMenuOpen ? "bg-secondary border-border" : "border-border hover:bg-secondary/50"}`}
                title={T("Add photos or videos")}
                data-testid="tickets-ai-attach"
              >
                {attaching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </button>
            </div>
            <button
              type="button"
              onClick={toggle}
              disabled={transcribing}
              className={`h-9 w-9 rounded-sm border flex items-center justify-center shrink-0 transition ${recording ? "bg-red-500 text-white border-red-500 animate-pulse" : "border-border hover:bg-secondary/50"}`}
              title={recording ? T("Tap to stop") : T("Ask by voice")}
              data-testid="tickets-ai-mic"
            >
              {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
            </button>
            <Input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
              placeholder={recording ? T("Listening…") : T("Ask about your tickets…")}
              className="h-9 rounded-sm text-sm flex-1 min-w-0"
              data-testid="tickets-ai-input"
            />
            <Button
              size="sm"
              className="rounded-sm h-9 shrink-0"
              onClick={() => ask()}
              disabled={asking || (!question.trim() && images.length === 0)}
              data-testid="tickets-ai-send"
            >
              {asking ? "…" : T("Ask")}
            </Button>
          </div>
        </div>
      )}
      {videoUrl && (
        <VideoFrameGrabber
          src={videoUrl}
          remaining={4 - images.length}
          onGrab={addGrabbedFrame}
          onClose={closeGrabber}
          T={T}
        />
      )}
      {markupIdx !== null && images[markupIdx] && (
        <ImageMarkup
          src={images[markupIdx]}
          onSave={(dataUrl) => {
            setImages((prev) => prev.map((im, i) => (i === markupIdx ? dataUrl : im)));
            setMarkupIdx(null);
          }}
          onClose={() => setMarkupIdx(null)}
          T={T}
        />
      )}
    </>
  );
  return typeof document !== "undefined" ? createPortal(widget, document.body) : widget;
}
